import { readonly, ref, computed } from "vue";
import { OrganisationScaleAPI } from "@/api";
import { mapOrganisationScales } from "@/utils/organisationScale";
import { getCookie, removeCookie, setCookie } from "@/utils/cookieUtil";
import type { Organisation, MappedOrganisationScale } from "@/types";

const currentOrganisation = ref<number | undefined>(undefined);
const currentTopOrganisation = ref<Organisation | null>(null);
const riskScales = ref<ReadonlyArray<MappedOrganisationScale>>([]);

/**
 * Set the current (sub) organisation ID, persisting to a cookie.
 * @param organisationId ID of the sub‑organisation, or null to clear.
 */
const setOrganisation = async (
  organisationId: number | undefined
): Promise<void> => {
  currentOrganisation.value = organisationId;
  if (organisationId !== undefined) {
    setCookie("subExposedId", organisationId?.toString());
  } else {
    removeCookie("subExposedId");
  }
};

/**
 * Fetch risk scales from the API for the current organisation.
 */
const fetchRiskScales = async (): Promise<void> => {
  const scales = await OrganisationScaleAPI.fetchAll();
  // Cast raw API response to Record<string,unknown> for mapping
  riskScales.value = mapOrganisationScales(scales[0]);
};

/**
 * Set the top‑level organisation, persist cookie, and fetch scales.
 * @param organisation Organisation object, or null to clear.
 */
const setTopOrganisation = async (
  organisation: Organisation | null
): Promise<void> => {
  currentTopOrganisation.value = organisation;
  if (organisation) {
    setCookie("exposedId", organisation.id.toString());
    await fetchRiskScales();
  } else {
    await setOrganisation(undefined);
    removeCookie("exposedId");
  }
};

/**
 * Initialize the top‑level organisation from cookie if available.
 * @param organisations List of all organisations.
 */
const initTopOrganisationFromCookie = async (
  organisations: Organisation[]
): Promise<void> => {
  const organisationId = getCookie("exposedId");
  if (!organisationId) return;

  const org = organisations.find((o) => o.id === Number(organisationId));

  if (org) {
    await setTopOrganisation(org);
  }
};

/**
 * Initialize the sub‑organisation from cookie if available.
 * @param organisations List of all organisations.
 */
const initSubOrganisationFromCookie = async (
  organisations: Organisation[]
): Promise<void> => {
  const subOrgId = getCookie("subExposedId");
  if (!subOrgId) return;

  const subOrg = organisations.find((o) => o.id === Number(subOrgId));
  if (subOrg) {
    await setOrganisation(subOrg.id);
  }
};

/**
 * Composable for selecting organisations in the UI.
 */
export function useSelectOrganisation() {
  return {
    currentOrganisation: computed<number | undefined>({
      get: () => currentOrganisation.value,
      set: setOrganisation,
    }),
    setOrganisation,
    currentTopOrganisation: computed<Organisation | null>({
      get: () => currentTopOrganisation.value,
      set: setTopOrganisation,
    }),
    setTopOrganisation,
    riskScales: readonly(riskScales.value),
    riskScaleReactive: riskScales,
    initTopOrganisationFromCookie,
    initSubOrganisationFromCookie,
  };
}
